import { cn } from '@/lib/utils';

/**
 * Single source of truth for production/project status chips. Previously
 * re-implemented with slightly different colours in Projects, ProjectDashboard and
 * Production.
 *
 * `status` comes from the engine, so unknown values must degrade gracefully rather
 * than render unstyled.
 */
export type PillTone = 'neutral' | 'running' | 'complete' | 'failed' | 'attention';

const TONE: Record<PillTone, string> = {
  neutral: 'border-border bg-muted/40 text-faint',
  running: 'border-success/30 bg-success/12 text-success',
  complete: 'border-success/35 bg-success/14 text-success',
  failed: 'border-destructive/30 bg-destructive/12 text-destructive',
  attention: 'border-primary/40 bg-primary/12 text-primary',
};

const STATUS_TONE: Record<string, PillTone> = {
  draft: 'neutral',
  idle: 'neutral',
  cancelled: 'neutral',
  starting: 'running',
  running: 'running',
  active: 'running',
  complete: 'complete',
  completed: 'complete',
  failed: 'failed',
  error: 'failed',
};

export function toneForStatus(status: string): PillTone {
  return STATUS_TONE[status?.toLowerCase()] ?? 'neutral';
}

export default function StatusPill({
  children,
  tone,
  status,
  className,
}: {
  children: React.ReactNode;
  /** Explicit tone; otherwise derived from `status`. */
  tone?: PillTone;
  status?: string;
  className?: string;
}) {
  const resolved = tone ?? toneForStatus(status ?? '');
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-0.5',
        'font-mono text-[10px] leading-4',
        TONE[resolved],
        className,
      )}
    >
      {children}
    </span>
  );
}
